import React, { useState, useEffect, useRef } from 'react';
import { apiService, Structure } from '../services/api';
import logger from '../utils/logger';

const StructureManager: React.FC = () => {
  const [structures, setStructures] = useState<Structure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ structure_name: '', alternate_phrases: '' });
  const [newForm, setNewForm] = useState({ structure_name: '', alternate_phrases: '' });

  const [generatingEmbeddings, setGeneratingEmbeddings] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadStructures();
  }, []);

  const loadStructures = async () => {
    try {
      setLoading(true);
      const data = await apiService.getStructures();
      setStructures(data);
    } catch (err) {
      setError('Failed to load structures');
      logger.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 4000);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newForm.structure_name.trim()) return;
    setError(null);

    try {
      await apiService.createStructure(
        newForm.structure_name.trim(),
        newForm.alternate_phrases.trim()
      );
      setNewForm({ structure_name: '', alternate_phrases: '' });
      showSuccess('Structure created successfully.');
      await loadStructures();
    } catch (err: any) {
      logger.error('Create error:', err);
      if (err.response?.status === 409) {
        setError(`Structure name '${newForm.structure_name.trim()}' already exists.`);
      } else {
        setError(err.response?.data?.error ?? 'Failed to create structure');
      }
    }
  };

  const handleUpdate = async (id: number) => {
    if (!editForm.structure_name.trim()) return;
    setError(null);

    try {
      await apiService.updateStructure(id, editForm.structure_name.trim(), editForm.alternate_phrases.trim());
      setEditingId(null);
      showSuccess('Structure updated successfully.');
      await loadStructures();
    } catch (err: any) {
      logger.error('Update error:', err);
      if (err.response?.status === 409) {
        setError(`Structure name '${editForm.structure_name.trim()}' already exists.`);
      } else {
        setError(err.response?.data?.error ?? 'Failed to update structure');
      }
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Delete structure "${name}"?`)) return;
    setError(null);

    try {
      await apiService.deleteStructure(id);
      showSuccess('Structure deleted.');
      await loadStructures();
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Failed to delete structure');
      logger.error('Delete error:', err);
    }
  };

  const startEdit = (s: Structure) => {
    setEditingId(s.id!);
    setEditForm({ structure_name: s.structure_name, alternate_phrases: s.alternate_phrases });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ structure_name: '', alternate_phrases: '' });
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);

    try {
      const result = await apiService.uploadStructures(file);
      let msg = `Processed ${result.totalProcessed} rows: ${result.insertedCount} inserted.`;
      if (result.duplicates.length > 0) msg += ` ${result.duplicates.length} duplicates skipped.`;
      if (result.errors.length > 0) msg += ` ${result.errors.length} errors.`;
      showSuccess(msg);
      if (result.errors.length > 0) {
        setError('Upload errors:\n' + result.errors.join('\n'));
      }
      await loadStructures();
    } catch (err: any) {
      logger.error('Upload error:', err);
      const msg = err.response?.data?.error ?? 'Failed to upload CSV';
      const details = err.response?.data?.details;
      setError(details && Array.isArray(details) ? `${msg}\n${details.join('\n')}` : msg);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async () => {
    try {
      const blob = await apiService.downloadStructures();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'structures.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download CSV');
      logger.error('Download error:', err);
    }
  };

  const handleGenerateEmbeddings = async () => {
    setGeneratingEmbeddings(true);
    setError(null);
    try {
      const result = await apiService.generateStructureEmbeddings();
      showSuccess(`${result.message} — ${result.success} succeeded, ${result.failed} failed.`);
      await loadStructures();
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Failed to generate embeddings');
      logger.error('Embedding generation error:', err);
    } finally {
      setGeneratingEmbeddings(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            <span className="ml-2 text-gray-600">Loading structures...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Add New Structure */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Add New Structure</h2>
        <form onSubmit={handleCreate} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Structure Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newForm.structure_name}
                onChange={(e) => setNewForm({ ...newForm, structure_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., Spine, Heart, Femur"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Alternate Phrases
              </label>
              <input
                type="text"
                value={newForm.alternate_phrases}
                onChange={(e) => setNewForm({ ...newForm, alternate_phrases: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., backbone, vertebral column, spinal column"
              />
              <p className="mt-1 text-xs text-gray-500">
                Comma-separated synonyms used for similarity search.
              </p>
            </div>
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-purple-600 text-white font-medium rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          >
            Add Structure
          </button>
        </form>
      </div>

      {/* Bulk Operations */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Bulk Operations</h3>
        <div className="flex flex-wrap gap-3">
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleUpload}
              className="hidden"
              id="structures-csv-upload"
            />
            <label
              htmlFor="structures-csv-upload"
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 cursor-pointer"
            >
              Upload CSV
            </label>
          </div>
          <button
            onClick={handleDownload}
            className="px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700"
          >
            Download CSV
          </button>
          <button
            onClick={handleGenerateEmbeddings}
            disabled={generatingEmbeddings}
            className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {generatingEmbeddings && (
              <span className="animate-spin inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            )}
            Generate Embeddings
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          CSV columns: <code className="bg-gray-100 px-1 rounded">structure_name</code> (required),{' '}
          <code className="bg-gray-100 px-1 rounded">alternate_phrases</code> (optional). Header row required.
          Embeddings are generated automatically on create/edit and after CSV upload. Use "Generate Embeddings" to
          backfill any missing ones.
        </p>
      </div>

      {/* Notifications */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 flex justify-between items-start">
          <p className="text-sm text-green-800">{successMessage}</p>
          <button onClick={() => setSuccessMessage(null)} className="text-green-600 hover:text-green-900 ml-4 text-lg leading-none">&times;</button>
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-1 text-sm text-red-700 whitespace-pre-line">{error}</div>
            </div>
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-800 ml-4 text-lg leading-none">&times;</button>
          </div>
        </div>
      )}

      {/* Structures Table */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Structures ({structures.length})
        </h3>

        {structures.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No structures found. Add some structures to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                    ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                    Structure Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Alternate Phrases
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    Created
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {structures.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-500">
                      #{s.id}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {editingId === s.id ? (
                        <input
                          type="text"
                          value={editForm.structure_name}
                          onChange={(e) => setEditForm({ ...editForm, structure_name: e.target.value })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                      ) : (
                        <span className="text-sm font-medium text-gray-900">{s.structure_name}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingId === s.id ? (
                        <input
                          type="text"
                          value={editForm.alternate_phrases}
                          onChange={(e) => setEditForm({ ...editForm, alternate_phrases: e.target.value })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
                          placeholder="comma-separated synonyms"
                        />
                      ) : (
                        <span className="text-sm text-gray-600">
                          {s.alternate_phrases || <span className="text-gray-400 italic">—</span>}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium space-x-2">
                      {editingId === s.id ? (
                        <>
                          <button
                            onClick={() => handleUpdate(s.id!)}
                            className="text-green-600 hover:text-green-900"
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="text-gray-500 hover:text-gray-900"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => startEdit(s)}
                            className="text-purple-600 hover:text-purple-900"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(s.id!, s.structure_name)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StructureManager;
